import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../../theme/ThemeContext';
import { usePermissions } from '../../context/PermissionContext';
import useDepartmentStore from '../../stores/departmentStore';
import AdminPageHeader from '../common/AdminPageHeader';
import { ConfirmationModal } from '../common';
import './DepartmentSettings.css';

/**
 * DepartmentSettings (EPIC A / A1) — admin department CRUD.
 *
 * Create / list / edit (rename + re-code) / soft-delete (deactivate) org
 * departments. Mirrors LabModuleSettings / ProcedureCatalogPage conventions:
 *   - gated on MANAGE_DEPARTMENT (early Navigate to /access-denied)
 *   - reads/writes the single departmentStore source of truth
 *   - member_count comes straight off each dept row (single source of truth);
 *     the deactivate confirm surfaces it so the admin knows how many staff are
 *     affected (allow-with-warning behaviour per A1-S4).
 *
 * Day-mode contrast + button-visibility rules from CLAUDE.md applied:
 * every surface sets an explicit light background + dark counterpart.
 */
export default function DepartmentSettings() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('MANAGE_DEPARTMENT');

  // ── Store ───────────────────────────────────────────────────────────
  const departments = useDepartmentStore((s) => s.departments);
  const includeInactive = useDepartmentStore((s) => s.includeInactive);
  const isLoading = useDepartmentStore((s) => s.isLoading);
  const error = useDepartmentStore((s) => s.error);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const setIncludeInactive = useDepartmentStore((s) => s.setIncludeInactive);
  const createDepartment = useDepartmentStore((s) => s.createDepartment);
  const updateDepartment = useDepartmentStore((s) => s.updateDepartment);
  const softDeleteDepartment = useDepartmentStore((s) => s.softDeleteDepartment);
  const reactivateDepartment = useDepartmentStore((s) => s.reactivateDepartment);
  const membersByDept = useDepartmentStore((s) => s.membersByDept);
  const fetchDeptMembers = useDepartmentStore((s) => s.fetchDeptMembers);

  // ── Create form ─────────────────────────────────────────────────────
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // ── Edit row ────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // ── Deactivate confirm + result alert ──────────────────────────────
  const [confirmDept, setConfirmDept] = useState(null);  // dept row
  const [deactivating, setDeactivating] = useState(false);
  const [resultAlert, setResultAlert] = useState(null);  // { title, message }

  // ── Reactivate ──────────────────────────────────────────────────────
  const [reactivatingId, setReactivatingId] = useState(null);

  // ── Expandable members tree (A1-S7 "list a department's members") ──
  // expandedIds: which dept rows are open. memberFetch: per-dept fetch state
  // (loading / error). Member rows themselves are cached in the store's
  // membersByDept (single source of truth) so re-expand is instant.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [memberFetch, setMemberFetch] = useState({}); // dept_id → {loading, error}

  const toggleExpand = async (dept) => {
    const id = dept.id;
    const isOpen = expandedIds.has(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(id);
      else next.add(id);
      return next;
    });
    // Collapsing, or already cached → nothing to fetch.
    if (isOpen || membersByDept[id]) return;
    // Lazy fetch on first expand.
    setMemberFetch((m) => ({ ...m, [id]: { loading: true, error: null } }));
    const result = await fetchDeptMembers(id);
    setMemberFetch((m) => ({
      ...m,
      [id]: { loading: false, error: result.success ? null : result.error },
    }));
  };

  useEffect(() => {
    if (canManage && !deptsLoaded) fetchDepartments();
  }, [canManage, deptsLoaded, fetchDepartments]);

  // ── Create ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setShowAdd(true);
    setNewName('');
    setNewCode('');
    setCreateError(null);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateError(null);
    if (!newName.trim()) {
      setCreateError('Department name is required.');
      return;
    }
    setCreating(true);
    const body = { name: newName.trim() };
    if (newCode.trim()) body.code = newCode.trim();
    const result = await createDepartment(body);
    setCreating(false);
    if (result.success) {
      setShowAdd(false);
      setNewName('');
      setNewCode('');
    } else {
      setCreateError(result.error);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────
  const startEdit = (dept) => {
    setEditingId(dept.id);
    setEditName(dept.name || '');
    setEditCode(dept.code || '');
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCode('');
    setEditError(null);
  };

  const saveEdit = async (dept) => {
    setEditError(null);
    if (!editName.trim()) {
      setEditError('Department name is required.');
      return;
    }
    setSavingEdit(true);
    const body = { name: editName.trim(), code: editCode.trim() || null };
    const result = await updateDepartment(dept.id, body);
    setSavingEdit(false);
    if (result.success) {
      cancelEdit();
    } else {
      setEditError(result.error);
    }
  };

  // ── Deactivate (soft-delete) ───────────────────────────────────────
  const confirmDeactivate = async () => {
    if (!confirmDept) return;
    setDeactivating(true);
    const result = await softDeleteDepartment(confirmDept.id);
    setDeactivating(false);
    const dept = confirmDept;
    setConfirmDept(null);
    if (result.success) {
      const count = result.data?.member_count ?? dept.member_count ?? 0;
      setResultAlert({
        title: 'Department deactivated',
        message: count > 0
          ? `“${dept.name}” is now hidden. ${count} staff member${count === 1 ? '' : 's'} remain assigned (memberships are kept and restored if you reactivate it).`
          : `“${dept.name}” is now hidden. You can reactivate it anytime by including inactive departments.`,
      });
    } else {
      setResultAlert({ title: 'Failed to deactivate', message: result.error });
    }
  };

  // ── Reactivate (un-delete) ─────────────────────────────────────────
  const handleReactivate = async (dept) => {
    setReactivatingId(dept.id);
    const result = await reactivateDepartment(dept.id);
    setReactivatingId(null);
    if (result.success) {
      setResultAlert({
        title: 'Department reactivated',
        message: `“${dept.name}” is active again. Existing staff memberships have been restored.`,
      });
    } else {
      setResultAlert({ title: 'Failed to reactivate', message: result.error });
    }
  };

  // ── Permission gate (after all hooks — Rules of Hooks) ─────────────
  if (!canManage) {
    return <Navigate to="/access-denied" replace />;
  }

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div className="dept-settings-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="dept-settings-main">
        {/* Title + add */}
        <div className="dept-settings-title-row">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              🏢 Departments
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-xl">
              Create your organisation&apos;s departments and assign staff to them
              from the Manage Staff screen. Deactivating a department hides it
              without removing staff memberships.
            </p>
          </div>
          <button
            type="button"
            onClick={openAdd}
            disabled={showAdd}
            className="shrink-0 text-sm font-semibold px-3 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
          >
            + Add Department
          </button>
        </div>

        {/* Include-inactive toggle */}
        <label className="inline-flex items-center gap-2 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 accent-teal-600"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Show deactivated departments
          </span>
        </label>

        {/* Create form */}
        {showAdd && (
          <form
            onSubmit={submitCreate}
            className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 mb-4 shadow-sm"
          >
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-3">
              New Department
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                  Name <span className="text-rose-600">*</span>
                </span>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. General Medicine"
                  autoFocus
                  disabled={creating}
                  className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                  Code (optional)
                </span>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder="e.g. GMED"
                  disabled={creating}
                  className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                />
              </label>
            </div>
            {createError && (
              <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{createError}</div>
            )}
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                disabled={creating}
                className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="text-sm font-semibold px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        )}

        {/* Loading / error */}
        {isLoading && (
          <div className="text-sm text-slate-600 dark:text-slate-400 py-6 text-center">
            Loading departments…
          </div>
        )}
        {error && !isLoading && (
          <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700 rounded-md p-3 mb-3">
            {error}
          </div>
        )}

        {/* List */}
        {!isLoading && !error && (
          departments.length === 0 ? (
            <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-8 text-center text-slate-600 dark:text-slate-400">
              <div className="text-3xl mb-2">🏢</div>
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
                No departments yet
              </h3>
              <p className="text-sm mt-1">Click “+ Add Department” to create your first one.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
              <div className="dept-table-wrap">
                <table className="dept-table">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-slate-600 dark:text-slate-400">Name</th>
                      <th className="text-slate-600 dark:text-slate-400">Code</th>
                      <th className="text-slate-600 dark:text-slate-400">Members</th>
                      <th className="text-slate-600 dark:text-slate-400">Status</th>
                      <th className="text-slate-600 dark:text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept) => {
                      const isEditing = editingId === dept.id;
                      if (isEditing) {
                        return (
                          <tr key={dept.id} className="border-b border-slate-100 dark:border-slate-700/60">
                            <td colSpan={5}>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-1">
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  placeholder="Name"
                                  disabled={savingEdit}
                                  className="text-sm px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                />
                                <input
                                  type="text"
                                  value={editCode}
                                  onChange={(e) => setEditCode(e.target.value)}
                                  placeholder="Code (optional)"
                                  disabled={savingEdit}
                                  className="text-sm px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                />
                              </div>
                              {editError && (
                                <div className="text-xs text-rose-600 dark:text-rose-400 px-1 mt-1">{editError}</div>
                              )}
                              <div className="dept-row-actions p-1 mt-1 justify-end">
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  disabled={savingEdit}
                                  className="text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(dept)}
                                  disabled={savingEdit}
                                  className="text-xs font-semibold px-2.5 py-1 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                                >
                                  {savingEdit ? 'Saving…' : 'Save'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      const isExpanded = expandedIds.has(dept.id);
                      const fetchState = memberFetch[dept.id] || {};
                      const members = membersByDept[dept.id];
                      return (
                        <React.Fragment key={dept.id}>
                        <tr
                          className="border-b border-slate-100 dark:border-slate-700/60"
                        >
                          <td className="font-medium text-slate-900 dark:text-slate-100">
                            <button
                              type="button"
                              onClick={() => toggleExpand(dept)}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? `Collapse ${dept.name} members` : `Expand ${dept.name} members`}
                              className="dept-caret-btn"
                            >
                              <span className={`dept-caret ${isExpanded ? 'is-open' : ''}`} aria-hidden="true">▸</span>
                              <span>{dept.name}</span>
                            </button>
                          </td>
                          <td className="text-slate-600 dark:text-slate-300">{dept.code || '—'}</td>
                          <td className="text-slate-700 dark:text-slate-200">{dept.member_count ?? '—'}</td>
                          <td>
                            <span
                              className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${
                                dept.active === false
                                  ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                              }`}
                            >
                              {dept.active === false ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                          <td>
                            <div className="dept-row-actions">
                              <button
                                type="button"
                                onClick={() => startEdit(dept)}
                                className="text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                              >
                                Edit
                              </button>
                              {dept.active !== false ? (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDept(dept)}
                                  className="text-xs font-medium px-2.5 py-1 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleReactivate(dept)}
                                  disabled={reactivatingId === dept.id}
                                  className="text-xs font-medium px-2.5 py-1 rounded-md border border-emerald-500 dark:border-emerald-600 bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-60"
                                >
                                  {reactivatingId === dept.id ? 'Reactivating…' : 'Reactivate'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="dept-members-row">
                            <td colSpan={5} className="dept-members-cell">
                              {fetchState.loading ? (
                                <div className="dept-members-state text-slate-600 dark:text-slate-300">
                                  Loading members…
                                </div>
                              ) : fetchState.error ? (
                                <div className="dept-members-state text-rose-700 dark:text-rose-300">
                                  {fetchState.error}
                                </div>
                              ) : !members || members.length === 0 ? (
                                <div className="dept-members-state text-slate-600 dark:text-slate-300">
                                  No members
                                </div>
                              ) : (
                                <ul className="dept-members-tree">
                                  {members.map((m) => (
                                    <li key={m.member_id} className="dept-member-node">
                                      <span className="dept-member-name text-slate-900 dark:text-slate-100">
                                        {m.name || m.member_id}
                                      </span>
                                      {m.role && (
                                        <span className="dept-member-role">{m.role}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
      </main>

      {/* Deactivate confirm — surfaces the member-count warning */}
      {confirmDept && (
        <ConfirmationModal
          isOpen={true}
          title="Deactivate Department"
          message={
            (confirmDept.member_count ?? 0) > 0
              ? `“${confirmDept.name}” has ${confirmDept.member_count} assigned staff member${confirmDept.member_count === 1 ? '' : 's'}. Deactivating hides it from the list but keeps those memberships (restored if you reactivate). Continue?`
              : `Deactivate “${confirmDept.name}”? It will be hidden but can be reactivated anytime.`
          }
          icon="⚠️"
          variant="danger"
          ctaLabel={deactivating ? 'Deactivating…' : 'Deactivate'}
          onConfirm={confirmDeactivate}
          onCancel={() => setConfirmDept(null)}
        />
      )}

      {/* Result alert */}
      {resultAlert && (
        <ConfirmationModal
          isOpen={true}
          title={resultAlert.title}
          message={resultAlert.message}
          icon="ℹ️"
          ctaLabel="OK"
          onConfirm={() => setResultAlert(null)}
        />
      )}
    </div>
  );
}
